import * as React from "react";
import { cn } from "@/lib/utils";
import { useScrollActivity } from "@/hooks/useScrollActivity";

/** thumb 최소 높이 — 항목이 아주 많아도 눌러 잡을 수 있을 만큼은 남긴다 */
const MIN_THUMB_HEIGHT = 24;

interface ThumbRect {
  top: number;
  height: number;
}

interface DropdownScrollAreaProps {
  /** 스크롤 박스에 붙는다 (max-h 등) */
  className?: string;
  /** 목록 래퍼에 붙는다 (flex / gap / padding 등) */
  contentClassName?: string;
  /** 팝오버 밖으로 휠 전파를 막아야 하는 경우 */
  onWheel?: React.WheelEventHandler<HTMLDivElement>;
  /** 스크롤 박스를 밖에서도 잡아야 하는 경우 (선택 항목 scrollIntoView 등) */
  scrollerRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

/**
 * 드롭다운 목록용 스크롤 영역.
 * 네이티브 스크롤바를 숨기고 thumb 을 목록 위에 겹쳐 그린다 — 네이티브 스크롤바는
 * 자기 폭만큼 콘텐츠 영역을 빼앗아, 스크롤이 생기는 순간 목록 폭이 줄어들기 때문이다.
 * thumb 은 유휴 상태에서 숨고 hover 또는 스크롤 중에만 보인다.
 */
export function DropdownScrollArea({
  className,
  contentClassName,
  onWheel,
  scrollerRef: externalScrollerRef,
  children,
}: DropdownScrollAreaProps) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollActivity = useScrollActivity();
  // null 이면 스크롤이 필요 없는 상태 — thumb 자체를 렌더하지 않는다
  const [thumb, setThumb] = React.useState<ThumbRect | null>(null);

  const measure = () => {
    const el = scrollerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const next: ThumbRect | null =
      scrollHeight <= clientHeight
        ? null
        : (() => {
            const height = Math.max(
              MIN_THUMB_HEIGHT,
              (clientHeight / scrollHeight) * clientHeight,
            );
            const progress = scrollTop / (scrollHeight - clientHeight);
            return { top: progress * (clientHeight - height), height };
          })();

    // 값이 같으면 새 객체를 만들지 않는다 — 아래 useLayoutEffect 가 매 렌더 도는데
    // 그대로 setState 하면 렌더 루프가 된다
    setThumb((prev) => {
      if (prev === next) return prev;
      if (
        prev &&
        next &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  };

  // 그룹을 펼치거나 검색으로 목록이 걸러지면 scrollHeight 만 달라지고 스크롤 박스
  // 자신의 크기는 그대로라 ResizeObserver 로는 안 잡힌다 — 매 렌더 다시 잰다
  React.useLayoutEffect(measure);

  // 스크롤 박스(창 크기 변경)와 목록(그룹 펼침 애니메이션) 양쪽을 관찰한다.
  // 트랜지션 중에는 리렌더가 없어, 목록을 안 보면 thumb 이 옛 높이에 머문다
  const observerRef = React.useRef<ResizeObserver | null>(null);
  const observe = (node: HTMLDivElement | null) => {
    if (!node) return;
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver(() => measure());
    }
    observerRef.current.observe(node);
  };

  React.useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <div className="group/scroll relative">
      <div
        ref={(node) => {
          scrollerRef.current = node;
          if (externalScrollerRef) externalScrollerRef.current = node;
          observe(node);
        }}
        // overscroll-contain: 목록 끝에서 더 스크롤해도 뒤 페이지가 따라 움직이지 않게
        className={cn(
          "overflow-y-auto overscroll-contain scrollbar-none",
          className,
        )}
        onWheel={onWheel}
        onScroll={() => {
          scrollActivity.onScroll();
          measure();
        }}
      >
        <div ref={observe} className={contentClassName}>
          {children}
        </div>
      </div>

      {thumb && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute right-1 w-1 rounded-full bg-labels-assistive",
            "opacity-0 transition-opacity duration-150 group-hover/scroll:opacity-100",
            scrollActivity.isScrolling && "opacity-100",
          )}
          style={{ top: thumb.top, height: thumb.height }}
        />
      )}
    </div>
  );
}

DropdownScrollArea.displayName = "DropdownScrollArea";
